import type {
    Aktivitet,
    FormField,
    FormFieldConstraint,
    FormFieldProperty,
} from '../../interfaces';
import type { Bygg } from '@forvalter/types/ForvalterCommonDAL';
import type { FormValuesType } from '../../components/FormContainer';
import { evaluateExpression } from '../expression';

/**
 * Finds a Camunda validation by name
 */
export const findCamundaValidation = (
    name: string,
    validation?: Array<FormFieldConstraint>,
): boolean => validation?.some((v) => v.name === name) ?? false;

/**
 * Finds a Camunda property value by ID
 */
export const findCamundaPropertyValue = (
    propId: string,
    properties?: Array<FormFieldProperty>,
): string | undefined => properties?.find((prop) => prop.id === propId)?.value;

/**
 * Checks if a Camunda property exists by ID
 */
export const findCamundaProperty = (
    propId: string,
    properties?: Array<FormFieldProperty>,
): boolean => properties?.some((prop) => prop.id === propId) ?? false;

/**
 * Determines if a region is visible based on property configuration
 */
function isVisibleByRegion(
    region: string | undefined,
    visibleByRegionProperty: string | undefined,
): boolean {
    if (!region) return false;
    if (!visibleByRegionProperty) return true;

    const regions = visibleByRegionProperty.split(',').map((s) => s.trim());
    return regions.includes(region);
}

/**
 * Determines if a form field should be hidden based on various properties
 */
export const formfieldIsHidden = (
    formfield: FormField,
    values: FormValuesType,
    regionContext?: string,
): boolean => {
    const { value, properties } = formfield;
    const hiddenProperty = findCamundaProperty('hidden', properties);
    const hiddenIfEmptyProperty = findCamundaProperty(
        'hiddenIfEmpty',
        properties,
    );
    const evaluatedHiddenIfEmpty =
        hiddenIfEmptyProperty && (!value || value === '' || value === '0');

    const visibleExpression = findCamundaPropertyValue(
        'visibleExpression',
        properties,
    );
    const evaluatedVisibleExpression =
        visibleExpression === undefined ||
        evaluateExpression<boolean>(visibleExpression, values);

    const visibleByRegionProperty = findCamundaPropertyValue(
        'visibleByRegion',
        properties,
    );

    const visibleByRegion =
        visibleByRegionProperty === undefined
            ? true
            : isVisibleByRegion(regionContext, visibleByRegionProperty);

    return (
        hiddenProperty ||
        evaluatedHiddenIfEmpty ||
        !evaluatedVisibleExpression ||
        !visibleByRegion
    );
};

/**
 * Determines if an activity should be hidden based on its visibleExpression
 */
export const activityIsHidden = (
    aktivitet: Aktivitet,
    values: FormValuesType,
): boolean => {
    const { visibleExpression } = aktivitet;
    if (!visibleExpression) return false;

    return !evaluateExpression<boolean>(visibleExpression, values);
};

export const formfieldIsReadOnly = (
    formField: FormField,
    regionContext?: string,
): boolean => {
    const { properties, validation } = formField;
    const isReadonly = findCamundaValidation('readonly', validation);

    const readonlyForRegionValue = findCamundaPropertyValue(
        `readonlyByRegion`,
        properties,
    );

    if (!readonlyForRegionValue) return isReadonly;

    const isReadonlyForRegion =
        readonlyForRegionValue
            ?.toLowerCase()
            .includes(regionContext?.toLowerCase() ?? '') ?? false;

    return isReadonly || isReadonlyForRegion;
};

export const formfieldIsRequired = (
    formField: FormField,
    regionContext?: string,
): boolean => {
    const { properties, validation } = formField;
    const isRequired = findCamundaValidation('required', validation);

    const requiredForRegionValue = findCamundaPropertyValue(
        `requiredByRegion`,
        properties,
    );

    if (!requiredForRegionValue) return isRequired;

    const isRequiredForRegion =
        requiredForRegionValue
            ?.toLowerCase()
            .includes(regionContext?.toLowerCase() ?? '') ?? false;

    return isRequired || isRequiredForRegion;
};

/**
 * Converts form fields array to form values object
 */
export const convertFormFieldstoFormValues = (
    formFields: Array<FormField>,
): FormValuesType => {
    const entries = formFields.map((f) => {
        return [f.id, f.value];
    });

    return Object.fromEntries(entries) as FormValuesType;
};

type parsedByggJsonString = {
    id: string;
    navn: string;
};

/**
 * Handles building option parsing and retrieval from list
 */
export const handleBuildingOptionByValue = (
    jsonString?: string,
    byggList?: Array<Bygg>,
): Bygg | undefined => {
    if (!jsonString) return undefined;

    try {
        const parsed = JSON.parse(jsonString) as parsedByggJsonString;
        const byggFromList = byggList?.find((item) => item.id === parsed.id);
        return byggFromList || ({ id: parsed.id, navn: parsed.navn } as Bygg);
    } catch (e) {
        return undefined;
    }
};
